import { eventBus } from "@/main";
import willowApi from "@/http/api-service";
import clone from "just-clone";
import {
    newDate, dateAdd, dateFormat, urlParams, redirectWithSessionParams, 
    getLocalStorageSessionItems, deleteSessionInLocalStorage 
} from "@/utils";
import { routerRedirectToStep, redirectToLandingUrl } from "@/router/router-redirect";
import router from "@/router/index";

export default {
    //  Path is a local path - EG "/reserve-appointment" or "/about-you"
    doRedirect({ state }, path) {
        redirectWithSessionParams(path, state.session.data);
    },
    getMetadata({ commit }) {
        commit("setClinicsIsLoading", true);
        commit("setDCsIsLoading", true);
        commit("setStripeProductsIsLoading", true);

        //  Metadata comes direct from Willow API
        return willowApi.get("/metadata")
            .then(response => {
                //  NB setDropdownDCs depends on state.clinics.data having been set
                commit("setServerTimeDiffSeconds", response.data.server_time);
                commit("setClinics", response.data.clinics.data);
                commit("setDropdownClinics");

                commit("setDCs", response.data.dcs.data);
                commit("setTypeaheadDCs");
                commit("setDropdownDCs");

                commit("setStripeProducts", response.data.stripe_products.data);

                commit("setSettings", response.data.settings);
                commit("setDropdownReferralSources", response.data.settings.referral_sources || []);

                commit("setClinicsIsLoading", false);
                commit("setDCsIsLoading", false);
                commit("setStripeProductsIsLoading", false);

                commit("setIsInitialised", { key: "metadata", value: true });
            })
            .catch(() => {
                commit("setClinicsIsLoading", false);
                commit("setDCsIsLoading", false);
                commit("setStripeProductsIsLoading", false);
                commit("showError", {
                    message: "Sorry - there was a problem getting some of the basic data required by our bookings app."
                })
            });
    },
    checkOfferCode(_, { route }) {
        if (!route.query.offer || !route.query.offer.match(/^[a-f0-9]{64}$/i)) {
            router.push("/bookings/");
        }

        return willowApi.get(`/check-offer/${route.query.offer}`).then(response => {
            if(response.data.sessionid) {
                router.push({
                    name: "reserve-appointment",
                    query: { sessionid: response.data.sessionid }
                });
            }
            else {
                router.push("/bookings/");
            }
        });
    },
    initialiseSession({ commit }) {
        const sessionItems = getLocalStorageSessionItems(), apiParams = urlParams();
        const offerCode = apiParams.offer ?? "";
        const offerCodeMatch = offerCode.match(/^[a-z0-9]{64}/);

        apiParams.offer = offerCodeMatch ? offerCodeMatch[0] : "";
        apiParams.session_id = sessionItems.sessionId;

        commit("setSessionIsLoading", true);
        commit("setSessionIsInitialised", false);
 
        return willowApi.get("/sessions", apiParams)
        .then(response => {
            response.data.data.is_new_session = (response.data.data.code != apiParams.session_id);
            commit("setServerTimeDiffSeconds", response.data.server_time);
            commit("setSession", response.data.data);
            commit("checkResponseForSessionExpired", response);
            commit("setSessionIsInitialised", true);
            commit("setSessionIsLoading", false);

            return response;
        });
    },
    saveSession(_, params) { return willowApi.post(`/sessions/${params.sessionid}`, params); },
    startAgain({ state }) {
        const customUrlCode = state.session.data?.custom_url_code ?? false;
        deleteSessionInLocalStorage();
        redirectToLandingUrl(customUrlCode);
    },
    webLog(_, page) { willowApi.webLogGet(`/log-${page}`); },
    saveSessionFilters({ commit, state }, filters) {
        const session = clone(state.session.data);

        session.filters_json = {
            location_id: filters.selectedLocation ? filters.selectedLocation.id : 0,
            is_referred: filters.isReferred ? true : false,
            referred_dc_id: filters.selectedReferredDC ? filters.selectedReferredDC.id : 0,
            dc_gender: filters.selectedDCGender || "n-a",
            pregnant: filters.selectedPregnantCateg || "n-a",
            age: filters.selectedAgeCateg || "7-plus-years"    
        };

        commit("setSessionIsLoading", true);
        commit("setSession", session);

        return willowApi.post("/save-session-filters", { session })
        .then(() => {
            commit("setSessionIsLoading", false);
        });
    },
    touchSession({ commit, state }) {
        const session = clone(state.session.data);

        commit("setSessionIsLoading", true);

        return willowApi.post("/touch-session", { session })
        .then((response) => {
            commit("setSession", response.data.session);
            commit("setSessionIsLoading", false);
        });
    },
    initialiseAvailability({ commit }) {
        commit("initialiseAvailability");
    },
    getAvailability({ commit, state, dispatch }) { 
        commit("clearSlotsAndAppointments");
        commit("setSlotsIsLoading", true);
        commit("setAppointmentsIsLoading", true);

        let from = dateFormat(newDate(), "YYYY-MM-DD");
        if(state.availability.params.slots?.startsAt) {
            from = dateFormat(state.availability.params.slots.startsAt, "YYYY-MM-DD");
        }

        return willowApi.post("/availability-by-session", {
            session: state.session.data, 
            from
        })
        .then(response => {
            // console.log(`ax.getSlotsAndAppointments.slots`, response.data.slots);
            commit("setSlots", response.data.slots.data);
            commit("setAppointments", response.data.appointments.data);
            commit("setAvailabilityResultsDCs", response.data.dcs || []);
            commit("compileAvailability");
            commit("setSlotsIsLoading", false);
            commit("setAppointmentsIsLoading", false);

            return response;
        })
        .catch(error => {
            const sessionExpired = error.response?.data?.session_expired ?? false;
            if(sessionExpired) {
                eventBus.$emit("toast", {
                    message: "Sorry - your session expired.",
                    variant: "warning"
                });
                dispatch("startAgain");
            }
            else {
                console.log(`ax.getAvailability.error`, error);
                
                const message = error.response?.data?.message ?? "Sorry - there was an error trying to get availability from our server.";
                eventBus.$emit("toast", {
                    message,
                    variant: "danger"
                });
            }

            commit("setSlotsIsLoading", false);
            commit("setAppointmentsIsLoading", false);
        }); 
    },
    getAvailabilityMoreAppointments({ commit, state }) {
        const av = state.availability;
        const location_id = state.session.data.filters_json.location_id;
        const params = {
            finishedLoadingAppointments: false,
            isFirstSetOfAppointments: false,
            startsAt: dateAdd(av.params.appointments.startsAt, 1, "week"),
            endsAt: dateAdd(av.params.appointments.endsAt, 1, "week")
        };

        commit("initialiseLoadMoreAppointments", params);

        commit("setAppointmentsIsLoading", true);
        return willowApi.get("/appointments", {
            session_id: state.session.data.code,
            location_id, 
            from: dateFormat(params.startsAt, "YYYY-MM-DD"), 
            to: dateFormat(params.endsAt, "YYYY-MM-DD")
        })
        .then(response => {
            commit("appendAppointments", response.data.data);
            commit("compileAvailability");
            commit("setAppointmentsIsLoading", false);
        });
    },
    reserveAppointment({ commit }, params) {
        return willowApi.post("/appointments/reserve", params)
        .then(response => {
            commit("setSession", response.data.session);
        });
    },
    expireHeldAppointment({ state, dispatch }) {
        const appointment_id = state.session.data.npc_json?.id ?? false;
        if(appointment_id === false) {
            return;
        }

        return willowApi.post("/appointments/expire-by-id", { appointment_id })
        .then(() => {
            dispatch("startAgain");
        });
    },
    saveAboutYou({ commit, state }, params) {
        commit("setSessionIsLoading", true);
        return willowApi.post(`/about-you`, params)
            .then(response => {
                this._vm.$analytics.fbq.event("Lead");
                commit("setSession", response.data.session);
                commit("setSessionIsLoading", false);
                if(response.data.checkout_data) {
                    commit("setCheckoutData", response.data.checkout_data);
                }
                if(response.data.session.is_completed) {
                    routerRedirectToStep("FINISHED", state.session.data);
                }
                else {
                    routerRedirectToStep("CHECKOUT", state.session.data);
                }

            });
    },
    processStripeRedirect({ commit }, params) {
        commit("setSessionIsLoading", true);
        return willowApi.post("/stripe-redirects", params);
    },
    resetError({ commit }) { commit("resetError"); },
};
